import type { FileWithLatestFileVersion } from "schemas/generated/file";

import * as React from "react";

import { DebouncedInput } from "@editor/components/common/designSystem/Input";
import { useInfinitePages } from "@editor/hooks/useInfinitePages";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";

import { DeploymentStatusTag } from "@/features/react-editor/components/DeploymentStatusTag";
import Button from "@replo/design-system/components/button/Button";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import twMerge from "@replo/design-system/utils/twMerge";
import { Plus, Search } from "lucide-react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useNavigate } from "react-router-dom";

import PageRenderer from "../PageRenderer";

function PageSelectionCardSkeleton() {
  return (
    <div className="flex flex-col gap-2 rounded-md">
      <div className="w-full aspect-[16/10] rounded border-0.5 border-border overflow-hidden">
        <Skeleton className="h-full w-full" />
      </div>

      <div className="flex flex-col gap-1">
        <div className="flex flex-row justify-between items-center gap-2">
          <Skeleton className="h-4 w-20 rounded" />
          <Skeleton className="h-4 w-12 rounded" />
        </div>
        <Skeleton className="h-4 w-1/2 rounded" />
      </div>
    </div>
  );
}

function PageSelectionCard({
  page,
  handlePageSelectionCardClick,
  isCurrentPage = false,
}: {
  page: FileWithLatestFileVersion;
  handlePageSelectionCardClick: () => void;
  isCurrentPage?: boolean;
}) {
  const [isHovered, setIsHovered] = React.useState(false);

  if (!page.pageRoute) {
    return null;
  }

  return (
    <div
      className={twMerge(
        "flex flex-col gap-2 rounded-md",
        isCurrentPage ? "opacity-50" : "cursor-pointer",
      )}
      onClick={isCurrentPage ? undefined : handlePageSelectionCardClick}
      onMouseEnter={() => {
        if (!isCurrentPage) {
          setIsHovered(true);
        }
      }}
      onMouseLeave={() => {
        setIsHovered(false);
      }}
    >
      <div className="w-full aspect-[16/10] rounded border-0.5 border-border overflow-hidden">
        <PageRenderer
          pageRoute={page.pageRoute}
          previewThumbnail
          className="h-full w-full pointer-events-none"
          isScrollable={false}
          overlayElements={
            isHovered ? (
              <div className="absolute inset-0 bg-sheet/25" />
            ) : undefined
          }
        />
      </div>
      <div className="flex flex-col gap-1">
        <div className="flex flex-row justify-between items-center gap-2">
          <div
            className={twMerge(
              "text-sm font-medium truncate",
              isHovered && "text-muted",
            )}
          >
            {page.name ?? "Untitled Page"}
          </div>
          <DeploymentStatusTag file={page} size="sm" />
        </div>
        <div className="text-xs text-muted truncate">{page.pageRoute}</div>
      </div>
    </div>
  );
}

function NewPageCard({ projectId }: { projectId: string }) {
  const [isHovered, setIsHovered] = React.useState(false);

  const navigate = useNavigate();

  return (
    <div
      className="flex flex-col gap-2 rounded-md cursor-pointer"
      onClick={() => {
        navigate(`/shops/${projectId}/home`);
      }}
      onMouseEnter={() => {
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        setIsHovered(false);
      }}
    >
      <div
        className={twMerge(
          "w-full aspect-[16/10] rounded border border-border border-dashed bg-sheet flex items-center justify-center",
          isHovered && "bg-sheet/25",
        )}
      >
        <Plus size={20} className="text-muted" />
      </div>
      <div className="flex flex-col gap-1">
        <div
          className={twMerge("text-sm font-medium", isHovered && "text-muted")}
        >
          New Page
        </div>
      </div>
    </div>
  );
}

function PageSelectionDropdown({
  projectId,
  handlePageSelectionCardClick,
  currentPageId,
  hideNewPageButton = false,
}: {
  projectId: string;
  handlePageSelectionCardClick: (page: FileWithLatestFileVersion) => void;
  currentPageId?: string;
  hideNewPageButton?: boolean;
}) {
  const [searchQuery, setSearchQuery] = React.useState("");
  const navigate = useNavigate();
  const logEvent = useLogAnalytics();

  const { pages, isLoading, fetchNextPage, hasNextPage } = useInfinitePages({
    projectId,
    search: searchQuery,
    limit: 12,
  });

  const isEmptyPages = pages?.length === 0 && !isLoading && !searchQuery;
  const isEmptySearch =
    pages?.length === 0 && !isLoading && searchQuery.length > 0;

  if (isEmptyPages) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-10">
        <img
          src="/images/empty-pages.svg"
          alt="No pages found"
          className="h-15 mb-3"
        />
        <p className="header-h4 mb-1">No other pages yet</p>
        <p className="text-sm text-muted mb-4">
          All your pages will show up here
        </p>
        <Button
          variant="primary"
          size="sm"
          onClick={() => {
            navigate(`/shops/${projectId}/home`);
          }}
        >
          Create a new page
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <DebouncedInput
        size="base"
        placeholder="Search by page name or path..."
        startEnhancer={<Search size={14} />}
        value={searchQuery}
        onValueChange={setSearchQuery}
        layoutClassName="h-8 mr-3"
      />
      {isEmptySearch ? (
        <div className="flex flex-col items-center justify-center h-full p-10">
          <img
            src="/images/empty-page-search.svg"
            alt="No pages found"
            className="h-15 mb-3"
          />
          <p className="header-h4 mb-1">No pages found</p>
          <p className="text-sm text-muted">Try adjusting your search</p>
        </div>
      ) : (
        <div
          className="flex-1 overflow-y-auto pt-2 mt-1 pb-3"
          id="page-selection-scroll-container"
        >
          <InfiniteScroll
            dataLength={pages.length}
            next={() => void fetchNextPage()}
            hasMore={hasNextPage}
            loader={
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
                {Array.from({ length: 3 }).map((_, index) => (
                  <PageSelectionCardSkeleton key={index} />
                ))}
              </div>
            }
            scrollableTarget="page-selection-scroll-container"
            className="pr-3"
          >
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {!hideNewPageButton && <NewPageCard projectId={projectId} />}

              {isLoading
                ? Array.from({ length: 11 }).map((_, index) => (
                    <PageSelectionCardSkeleton key={index} />
                  ))
                : pages?.map((page) => (
                    <PageSelectionCard
                      key={page.id}
                      page={page}
                      isCurrentPage={page.id === currentPageId}
                      handlePageSelectionCardClick={() => {
                        logEvent("editor.nav.pages.select");
                        handlePageSelectionCardClick(page);
                      }}
                    />
                  ))}
            </div>
          </InfiniteScroll>
        </div>
      )}
    </div>
  );
}

export default PageSelectionDropdown;
